import React, { useState, useEffect } from 'react';
import { Box, VStack, HStack, Switch, Divider, Checkbox } from 'native-base';
import AmountDropdown from './dropdowns/Amount';
import PaymentDropdown from './dropdowns/Payment';
import { useFormStyles, useGeneralStyles, useTopNavigationStyles } from '~/shared/styles';
import {
    GiftCard,
    GiftCardPayment,
    observer,
    i18nFn,
    useActivateGiftCard,
    useReloadGiftCard,
    useSpringBigStore,
    useCreateGiftCardWithPoints,
    getMenuItems
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import ButtonSpinner from '~/shared/ButtonSpinner';
import Button from '~/shared/Button';
import PointsConverterField from '~/pages/giftCard/pointsConverter/PointsConverterField';
import * as yup from 'yup';
import { useController, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { SpIcon } from '~/shared/Icon';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import Pressable from '~/shared/Pressable';

const AddFunds = ({
    manageGiftCard,
    t,
    successToast,
    isPurchaseWithPoints,
    setGiftCardValue,
    setIsCompletedModalOpen,
    isConfirmState,
    setIsConfirmState,
}: {
    manageGiftCard: GiftCard | null,
    t: i18nFn,
    successToast: (title: string, description?: string) => void,
    isPurchaseWithPoints: boolean;
    setGiftCardValue: (value: number) => void,
    setIsCompletedModalOpen: (value: boolean) => void,
    isConfirmState: boolean
    setIsConfirmState: (value: boolean) => void
}) => {

    const {
        member,
        merchant,
        giftCards,
        giftCardPresetAmounts,
    } = useSpringBigStore();

    const maxAmount = Math.min(
        Number(member?.remaining_dollar_amount_for_current_period) / 100,
        Number(merchant?.gc_dollar_amount_one_time)
    );

    const minAmount = Math.min(merchant.gc_min_price, 20);

    const [isFundWithPoints, setIsFundWithPoints] = useState(isPurchaseWithPoints);
    const [errorCVV, setErrorCVV] = useState<string>('');
    const [convertPoints, setConvertPoints] = useState(0);
    const [pointsAmount, setPointsAmount] = useState(0);
    const [amount, setAmount] = useState<number>(0); // remove ability to purchase GC altogether
    // const [amount, setAmount] = useState<number>(merchant.gift_card_restrictions ? maxAmount : 20.00);
    const [belowAmount, setbelowAmount] = useState<number>(
        parseFloat(manageGiftCard?.auto_reload_amount?.split('$')[0] || '20')
    );
    const [paymentMethod, setPaymentMethod] = useState<GiftCardPayment | null>(null);
    const [submitError, setSubmitError] = useState('');
    const {
        icon
    } = useTopNavigationStyles();

    const {
        mutate: requestActivateGiftCard,
        error: requestActivateCardError,
        isLoading: activateGiftCardLoading
    } = useActivateGiftCard();

    const {
        mutate: reloadGiftCard,
        error: reloadGiftCardError,
        isLoading: reloadGiftCardLoading
    } = useReloadGiftCard();

    const {
        mutate: createWithPoints,
        error: createWithPointsError,
        isLoading: createWithPointsLoading,
    } = useCreateGiftCardWithPoints();


    const [isPaymentDisclaimer, setIsPaymentDisclaimer] = useState<boolean>(!JSON.parse(localStorage.getItem('isPaymentDisclaimerShown') || 'false'));

    const onConfirmPaymentDisclaimer = (): void => {
        localStorage.setItem('isPaymentDisclaimerShown', JSON.stringify(true));
        setIsPaymentDisclaimer(false);
    };

    useEffect(() => {

        requestActivateCardError && setSubmitError(t('general.submitError'));
    }, [requestActivateCardError, t]);

    useEffect(() => {

        reloadGiftCardError && setSubmitError(t('general.submitError'));
    }, [reloadGiftCardError, t]);

    const gStyle = useGeneralStyles();
    const isLoading = activateGiftCardLoading || reloadGiftCardLoading || createWithPointsLoading;
    const isBusy = !member || !merchant || isLoading;
    const isSubmitEnabled =
        (
            ((isFundWithPoints && pointsAmount) || ((paymentMethod && amount) || convertPoints)) &&
            !isBusy &&
            (!merchant?.gift_card_restrictions || amount <= maxAmount)
        );
    const [acknowledged, setAcknowledged] = useState(false);
    const {
        input_checkbox_background,
        input_checkbox_border,
        input_checkbox_icon
    } = useFormStyles();

    const schema = yup.object().shape({
        cvv: yup
            .string()
            .required(t('validation.cvv_required'))
            .trim()
            .matches(/^[0-9]{3,4}$/, t('validation.cvv_invalid')),
    });

    const { control, formState: { errors } } = useForm({
        resolver: yupResolver(schema)
    });

    const { field: cvvField } = useController({
        control,
        defaultValue: '',
        name: 'cvv',
    });

    const onSubmitReload = (): void => {

        // if (cvvField.value.length < 3 || cvvField.value.length > 4) {
        //     setErrorCVV('cvv is required');
        // }
        // else {
        reloadGiftCard({
            member_id: member?.id.toString(),
            card_pin: manageGiftCard.card_pin,
            card_number: manageGiftCard.card_number,
            // cvv: cvvField.value,
            balance: amount.toString(),
            auto_reload: manageGiftCard.auto_reload,
            auto_reload_amount: (belowAmount.toString() || '20.00'),
            purchase_location: '0',
            gift_card_payment_id: paymentMethod?.id || 0,
        }, {
            onError: (_) => {

                setAcknowledged(false);
                setIsConfirmState(false);
            },
            onSuccess: (_) => {

                observer.emit('close-actionsheet');
                setGiftCardValue(amount);
                setIsCompletedModalOpen(true);
                setIsConfirmState(false);
            }
        });
        // }
    };

    const onAddFunds = () => {

        if (!isSubmitEnabled) {
            return;
        }

        if (isFundWithPoints) {
            createWithPoints(
                {
                    member_id: member.id,
                    convert_points: convertPoints,
                    purchase_location: '0'
                },
                {
                    onSuccess: (_) => {

                        observer.emit('close-actionsheet');
                        setGiftCardValue(convertPoints);
                        setIsCompletedModalOpen(true);
                        setIsConfirmState(false);
                    },
                },
            );
        }
        else {
            // no cards yet or there are only closed-loop cards => create a new one
            if (!manageGiftCard || giftCards.every(card => card.is_closed_loop)) {
                requestActivateGiftCard({
                    balance: amount.toString(),
                    auto_reload: !!belowAmount,
                    auto_reload_amount: belowAmount.toString() || '0.00',
                    purchase_location: '0',
                    gift_card_payment_id: paymentMethod?.id || 0,
                    member_id: member?.id.toString(),
                }, {
                    onSuccess: (_) => {

                        observer.emit('close-actionsheet');
                        setGiftCardValue(convertPoints);
                        setIsCompletedModalOpen(true);
                        setIsConfirmState(false);
                    }
                });
                return;
            }

            // else reload the reloadable one
            onSubmitReload();
        }
    };

    const calculateTotal = (number: number, string: string | undefined) => {
        if (string) {
            const numericValue = parseFloat(string.replace('$', ''));
            const sum = number + numericValue;
            return `\$${sum.toFixed(2)}`;
        }

        return `\$${number.toFixed(2)}`;
    };

    if (isConfirmState) {
        return (<VStack width="100%" alignItems="center">
            <HStack width="100%" alignItems="center" justifyContent="space-between">
                <Pressable onPress={() => setIsConfirmState(false)}>
                    <SpIcon icon={faChevronLeft} color={icon}/>
                </Pressable>
                <Caption fontSize={24} flex={1} textAlign={'center'}>{t('giftCard.addFunds.confirmation.confirm_payment')}</Caption>
            </HStack>
            <Caption textAlign={'center'} py={6}>
                {t('giftCard.addFunds.confirmation.review_transaction_details')}
            </Caption>
            <VStack space={3} mb={6} w={'100%'}>
                <Box borderWidth={1} borderRadius="md" borderColor={gStyle.button_background} p={4}>
                    <HStack justifyContent="space-between" mb={3}>
                        <Caption>
                            {t('giftCard.addFunds.confirmation.load_amount')}
                        </Caption>
                        <Caption>$ {amount.toFixed(2)}</Caption>
                    </HStack>
                    {member?.customer_gift_card_fee &&
                member?.customer_gift_card_fee !== '$0.00' &&
            <HStack justifyContent="space-between" mb={3}>
                <Caption>
                    {t('giftCard.addFunds.confirmation.fee')}
                </Caption>
                <Caption>+ {member?.customer_gift_card_fee}</Caption>
            </HStack>}
                    <Divider />
                    <HStack justifyContent="space-between">
                        <Caption fontWeight="bold">
                            {t('giftCard.addFunds.confirmation.total_amount')}
                        </Caption>
                        <Caption fontWeight="bold" color={gStyle.button_background}>
                            {calculateTotal(amount, member?.customer_gift_card_fee)}
                        </Caption>
                    </HStack>
                </Box>
                <VStack space={3} mb={2}>
                    <Caption fontSize="sm" color="gray.500" textAlign={'center'}>
                        {t('giftCard.addFunds.confirmation.payment_method')}
                        <Caption fontWeight="bold" color={gStyle.button_background}>
                    xxxx-xxxx-xxxx-{paymentMethod?.card_nickname}
                        </Caption>
                    </Caption>
                    <HStack>
                        <Caption fontSize="xs" opacity={0.5}>
                            {t('giftCard.addFunds.confirmation.charges')}
                        </Caption>
                        <Caption fontSize="xs">&quot;Springbig&quot;</Caption>
                    </HStack>
                </VStack>
                <Box bg="#FFF9C4" p="4" rounded="lg" shadow="2" w={'100%'} >
                    <VStack space="4">
                        <Caption fontSize="lg" fontWeight="bold" textAlign="center">
                            {t('giftCard.addFunds.confirmation.important_payment_notice')}
                        </Caption>
                        <Caption fontSize="sm" textAlign="center">
                            {t('giftCard.addFunds.confirmation.by_using_this_card')}
                        </Caption>
                        <Caption fontSize="xs" textAlign="left">
                            {t('giftCard.addFunds.confirmation.all_payments')}
                            {t('giftCard.addFunds.confirmation.fraudulent')}
                        </Caption>
                        <VStack space="2" pl="4">
                            <Caption fontSize="xs" color="gray.700">
                                {t('giftCard.addFunds.confirmation.legal_penalties')}
                            </Caption>
                            <Caption fontSize="xs" color="gray.700">
                                {t('giftCard.addFunds.confirmation.account_closure')}
                            </Caption>
                            <Caption fontSize="xs" color="gray.700">
                                {t('giftCard.addFunds.confirmation.credit_damage')}
                            </Caption>
                        </VStack>
                        <HStack space="3" alignItems="center" justifyContent="center">
                            <Checkbox
                                isChecked={acknowledged}
                                bg={input_checkbox_background}
                                borderColor={input_checkbox_border}
                                _icon={{ color: input_checkbox_icon }}
                                onChange={(isChecked) => setAcknowledged(isChecked)}
                                value="acknowledge"
                            />
                            <Caption fontSize="sm">{t('giftCard.addFunds.confirmation.i_acknowledge')}</Caption>
                        </HStack>
                    </VStack>
                </Box>
                <Button
                    size="lg"
                    borderRadius="md"
                    bg={gStyle.button_background}
                    _text={{ color: gStyle.button_text }}
                    onPress={onAddFunds}
                    isDisabled={!acknowledged}
                >
                    {t('giftCard.addFunds.confirmation.complete_payment')}
                </Button>

                <Caption mt={2} fontSize="xs" textAlign="center" color="gray.400">
                    {t('giftCard.addFunds.confirmation.please_acknowledge')}
                </Caption>
            </VStack>
        </VStack>
        );
    }

    if (isPaymentDisclaimer) {
        return (
            <VStack space={8}>
                <VStack p={4} space={4} rounded={'lg'} w={'100%'} bg="#fdf8c1" shadow={2}>
                    <Caption textAlign={'center'} fontSize={'xl'} fontWeight={'bold'}>
                        Important Payment Notice
                    </Caption>
                    <Caption>
                        Dear Valued Member,
                    </Caption>
                    <Caption>
                        To continue using our wallet feature, please update your payment details.
                    </Caption>
                    <Caption>
                        <b>First-time users:</b> Kindly enter your credit/debit card information to start adding funds to your wallet.
                    </Caption>
                    <Caption>
                        <b>Returning users:</b> Due to recent updates to enhance security and functionality, we kindly ask you to re-enter your payment details.
                    </Caption>
                    <Caption>
                        We appreciate your time and understanding as we work to improve your experience.
                    </Caption>
                    <Caption>
                        Thank you for your continued support.
                    </Caption>
                </VStack>
                <Button
                    onPress={onConfirmPaymentDisclaimer}
                    backgroundColor={gStyle.button_background}
                    borderRadius={10}
                    width='55%'
                    alignSelf={'center'}
                >
                    <Caption
                        color={gStyle.button_text}
                        fontSize={20}
                    >
                      proceed
                    </Caption>
                </Button>
            </VStack>
        );
    }

    return (
        <Box width='100%' alignItems='center' >
            {
                !merchant?.hide_gift_card_payments &&
                <VStack justifyContent={'center'} alignItems={'center'} p={4} bg={'#F48309'}>
                    <Caption color={'white'}>{ merchant?.gift_card_alias } purchases or reloads are temporarily unavailable.</Caption>
                </VStack>
            }
            <Caption fontSize={24}>
                { t('giftCard.addFunds.title') }
            </Caption>
            <VStack width='100%' space={3} flexGrow={1} mt={8} >
                {
                    merchant.points_cash_conversion_enabled &&
                    merchant.create_gift_card_with_points &&
                    (!manageGiftCard || giftCards.every(card => card.is_closed_loop)) &&
                    member.balance >= parseFloat(merchant.dollars_per_point) &&
                    <HStack justifyContent={'space-between'}>
                        <Caption fontSize={18} fontWeight={'300'}>
                            { t('giftCard.addFunds.fundWithPoints', [merchant?.gift_card_alias]) }
                        </Caption>
                        <Switch
                            onTrackColor={gStyle.button_background}
                            isChecked={isFundWithPoints}
                            onToggle={() => setIsFundWithPoints(!isFundWithPoints)}
                        />
                    </HStack>
                }
                {
                    false && // remove ability to purchase GC altogether
                    !isFundWithPoints &&
                    merchant &&
                    !merchant.hide_gift_card_payments &&
                    <VStack>
                        <Caption fontSize={18} fontWeight={'300'}>
                            { t('general.amount') }
                        </Caption>
                        <AmountDropdown
                            t={t}
                            amount={amount}
                            setAmount={setAmount}
                            amounts={getMenuItems(merchant, giftCardPresetAmounts, maxAmount, minAmount)}
                        />
                    </VStack>
                }
                {
                    false && // remove ability to purchase GC altogether
                    !isFundWithPoints &&
                    merchant &&
                    !merchant.hide_gift_card_payments &&
                    <VStack>
                        <Caption fontSize={18} fontWeight={'300'}>
                            { t('general.payment') }
                        </Caption>
                        <PaymentDropdown
                            paymentMethod={paymentMethod}
                            setPaymentMethod={setPaymentMethod}
                            t={t}
                        />
                    </VStack>
                }
                {/*{*/}
                {/*    !isFundWithPoints && !(!manageGiftCard || giftCards.every(card => card.is_closed_loop)) &&*/}
                {/*    <FormControl flex={1} isInvalid={!!errorCVV}>*/}
                {/*        <FormControl.Label color={input_label}>*/}
                {/*            { t('credit_card.cvv') }*/}
                {/*        </FormControl.Label>*/}
                {/*        <Input*/}
                {/*            {...cvvField}*/}
                {/*            keyboardType={'numberPad'}*/}
                {/*            backgroundColor={input_background}*/}
                {/*            borderColor={input_border}*/}
                {/*            height={10}*/}
                {/*            fontSize={16}*/}
                {/*            color={input_text}*/}
                {/*            isInvalid={!!errors.cvv}*/}
                {/*            isDisabled={isLoading}*/}
                {/*            maxLength={4}*/}
                {/*            returnKeyType='done'*/}
                {/*        />*/}
                {/*        <FormControl.ErrorMessage*/}
                {/*            leftIcon={<WarningIcon size={3} />}*/}
                {/*        >*/}
                {/*            { errorCVV }*/}
                {/*        </FormControl.ErrorMessage>*/}
                {/*    </FormControl>*/}
                {/*}*/}
                { isFundWithPoints &&
                    <PointsConverterField t={t} onChange={(value) => setConvertPoints(value)} />
                }
                {
                    !isFundWithPoints &&
                    member?.customer_gift_card_fee &&
                    member?.customer_gift_card_fee !== '$0.00' &&
                    <Caption textAlign='center'>
                        { t('giftCard.addFunds.conditions.fees', [member?.customer_gift_card_fee]) }
                    </Caption>
                }
                {
                    (
                        requestActivateCardError || reloadGiftCardError
                    ) &&
                    <CaptionError>
                        {
                            (requestActivateCardError as any)?.data?.message ||
                            (reloadGiftCardError as any)?.data?.message ||
                            (createWithPointsError as any)?.data?.message
                        }
                    </CaptionError>
                }
                <Button
                    onPress={() => setIsConfirmState(true)}
                    backgroundColor={gStyle.button_background}
                    borderRadius={10}
                    isDisabled={!isSubmitEnabled}
                    width='55%'
                    alignSelf={'center'}
                >
                    { isLoading ?
                        <ButtonSpinner />
                        :
                        <Caption
                            color={gStyle.button_text}
                            fontSize={20}
                        >
                            { t('general.add')} ${isFundWithPoints ? convertPoints / parseFloat(merchant?.dollars_per_point || '0.0') : amount }
                        </Caption>
                    }
                </Button>
            </VStack>
        </Box>
    );
};

export default AddFunds;
